import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, normalizeClass as _normalizeClass, unref as _unref, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "py-2" }
const _hoisted_2 = { class: "flex items-center gap-3" }
const _hoisted_3 = ["value", "onClick"]
const _hoisted_4 = { class: "text-sm whitespace-nowrap" }
const _hoisted_5 = { key: 0 }
const _hoisted_6 = { class: "py-2" }
const _hoisted_7 = ["value", "onClick"]
const _hoisted_8 = { class: "py-2" }
const _hoisted_9 = ["value", "onClick"]
const _hoisted_10 = { class: "py-2" }
const _hoisted_11 = ["value", "onClick"]
const _hoisted_12 = {
  key: 0,
  class: "text-red-500 text-sm mt-1"
}
const _hoisted_13 = { class: "py-2" }
const _hoisted_14 = { class: "flex gap-2" }
const _hoisted_15 = ["value"]
const _hoisted_16 = {
  key: 1,
  class: "py-1 text-sm text-gray-500"
}
const _hoisted_17 = {
  key: 2,
  class: "py-2"
}
const _hoisted_18 = { class: "flex gap-1 mt-1" }
const _hoisted_19 = ["onClick"]

import { computed, ref } from 'vue'
import { Switch } from '@headlessui/vue'
import { useI18n } from 'vue-i18n'
import DropdownComponent from '@/components/tools/DropdownComponent.vue'
import {
  CouponScheduleRepeatUnitEnum,
  CouponScheduleDayOfWeekEnum,
} from '@/views/coupon/type/coupon-condition.type'
import 'v-calendar/dist/style.css'

export interface ScheduleFormData {
  scheduleStartDate: string | null
  scheduleEndDate: string | null
  scheduleAllDay: boolean
  scheduleStartTime: string | null
  scheduleEndTime: string | null
  scheduleRepeatAmount: number | null
  scheduleRepeatUnit: CouponScheduleRepeatUnitEnum
  scheduleDaysOfWeek: CouponScheduleDayOfWeekEnum[]
}


export default /*#__PURE__*/_defineComponent({
  __name: 'CouponScheduleSection',
  props: {
    modelValue: null
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit }: { emit: ({ (e: 'update:modelValue', value: ScheduleFormData): void }), expose: any, slots: any, attrs: any }) {

const props = __props as { modelValue: ScheduleFormData }

const ALL_DAYS = [
  CouponScheduleDayOfWeekEnum.SUN,
  CouponScheduleDayOfWeekEnum.MON,
  CouponScheduleDayOfWeekEnum.TUE,
  CouponScheduleDayOfWeekEnum.WED,
  CouponScheduleDayOfWeekEnum.THU,
  CouponScheduleDayOfWeekEnum.FRI,
  CouponScheduleDayOfWeekEnum.SAT,
]




const { t } = useI18n()
const endDateError = ref<string | null>(null)

const repeatUnitOptions = computed(() => [
  { label: t('messages.coupon.repeatUnit_none'), value: CouponScheduleRepeatUnitEnum.NONE },
  { label: t('messages.coupon.repeatUnit_day'), value: CouponScheduleRepeatUnitEnum.DAY },
  { label: t('messages.coupon.repeatUnit_week'), value: CouponScheduleRepeatUnitEnum.WEEK },
  { label: t('messages.coupon.repeatUnit_month'), value: CouponScheduleRepeatUnitEnum.MONTH },
])

const selectedRepeatUnit = computed(
  () => repeatUnitOptions.value.find(o => o.value === props.modelValue.scheduleRepeatUnit) ?? repeatUnitOptions.value[0]
)

// Parse day-of-month directly from YYYY-MM-DD to avoid timezone offset issues
const dayOfMonth = computed(() => {
  if (!props.modelValue.scheduleStartDate) return '—'
  return parseInt(props.modelValue.scheduleStartDate.split('-')[2], 10)
})

function dateStringToDate(dateStr: string | null): Date | null {
  if (!dateStr) return null
  const [year, month, day] = dateStr.split('-').map(Number)
  return new Date(year, month - 1, day)
}

function dateToDateString(date: Date | null): string | null {
  if (!date) return null
  const y = date.getFullYear()
  const m = (date.getMonth() + 1).toString().padStart(2, '0')
  const d = date.getDate().toString().padStart(2, '0')
  return `${y}-${m}-${d}`
}

function timeStringToDate(time: string | null): Date | null {
  if (!time || time === '') return null
  const [hours, minutes] = time.split(':').map(Number)
  const d = new Date()
  d.setHours(hours, minutes, 0, 0)
  return d
}

function dateToTimeString(date: Date | null): string | null {
  if (!date) return null
  return date.getHours().toString().padStart(2, '0') + ':' + date.getMinutes().toString().padStart(2, '0')
}

function update<K extends keyof ScheduleFormData>(field: K, value: ScheduleFormData[K]) {
  if(value === null || value === '') {
    value = null as ScheduleFormData[K];
  }

  emit('update:modelValue', { ...props.modelValue, [field]: value })
}

function updatetime<K extends 'scheduleStartTime' | 'scheduleEndTime'>(field: K, value: ScheduleFormData[K]) {
  if(value === null || value === '') {
    value = '00:00' as ScheduleFormData[K] // Default to 00:00 if time is cleared, since time fields can't be null when All Day is off
  }

  emit('update:modelValue', {
    ...props.modelValue,
    [field]: value,
    scheduleAllDay: false, // Turning on time should disable All Day
  })
}

function onToggleAllDay() {
  const newAllDay = !props.modelValue.scheduleAllDay

  if (newAllDay) {
    // If turning on All Day, clear time errors
    endDateError.value = null
    
    emit('update:modelValue', {
    ...props.modelValue,
    scheduleAllDay: newAllDay,
    scheduleStartTime: null,
    scheduleEndTime: null,
  })
  } else {
    emit('update:modelValue', {
      ...props.modelValue,
      scheduleAllDay: newAllDay,
      scheduleStartTime: props.modelValue.scheduleStartTime ?? '00:00',
      scheduleEndTime: props.modelValue.scheduleEndTime ?? '23:59',
    })
  }
}

function onSelectRepeatUnit(option: { label: string; value: CouponScheduleRepeatUnitEnum }) {
  endDateError.value = null
  emit('update:modelValue', {
    ...props.modelValue,
    scheduleRepeatUnit: option.value,
    scheduleDaysOfWeek: option.value === CouponScheduleRepeatUnitEnum.WEEK
      ? props.modelValue.scheduleDaysOfWeek
      : [],
  })
}

function toggleDay(day: CouponScheduleDayOfWeekEnum) {
  const days = props.modelValue.scheduleDaysOfWeek
  const newDays = days.includes(day) ? days.filter(d => d !== day) : [...days, day]
  update('scheduleDaysOfWeek', newDays)
}

function onChangeEndDate(value: string | null) {
  endDateError.value = null

  if(value === null || value === '') {
    update('scheduleEndDate', null)
    return
  }

  if (
    props.modelValue.scheduleRepeatUnit === CouponScheduleRepeatUnitEnum.MONTH &&
    props.modelValue.scheduleStartDate
  ) {
    const startParts = props.modelValue.scheduleStartDate.split('-').map(Number)
    const endParts = value.split('-').map(Number)
    const startDate = new Date(startParts[0], startParts[1] - 1, startParts[2])
    const oneMonthAfterStart = new Date(startDate.getFullYear(), startDate.getMonth() + 1, startDate.getDate()).getTime()
    const endMs = new Date(endParts[0], endParts[1] - 1, endParts[2]).getTime()
    if (endMs < oneMonthAfterStart) {
      endDateError.value = t('messages.coupon.scheduleEndDateError')
      return
    }
  }
  update('scheduleEndDate', value)
}

return (_ctx: any,_cache: any) => {
  const _component_v_date_picker = _resolveComponent("v-date-picker")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("div", _hoisted_1, [
      _createTextVNode(_toDisplayString(_ctx.$t('messages.coupon.scheduleStartDate')) + " ", 1),
      _createElementVNode("div", _hoisted_2, [
        _createVNode(_component_v_date_picker, {
          mode: "date",
          "model-value": dateStringToDate(__props.modelValue.scheduleStartDate),
          "onUpdate:modelValue": _cache[1] || (_cache[1] = (val) => update('scheduleStartDate', dateToDateString(val))),
          masks: { input: 'DD/MM/YYYY' },
          locale: "th"
        }, {
          default: _withCtx(({ inputValue, togglePopover }) => [
            _createElementVNode("input", {
              type: "text",
              value: inputValue,
              onClick: togglePopover,
              onChange: _cache[0] || (_cache[0] = (e) => { update('scheduleStartDate', (e.target as HTMLInputElement).value) }),
              class: "flex-1 rounded-md",
              placeholder: "วัน/เดือน/ปี (DD/MM/YYYY)"
            }, null, 40, _hoisted_3)
          ]),
          _: 1
        }, 8, ["model-value"]),
        _createElementVNode("span", _hoisted_4, _toDisplayString(_ctx.$t('messages.coupon.scheduleAllDay')), 1),
        _createVNode(_unref(Switch), {
          "model-value": __props.modelValue.scheduleAllDay,
          onClick: onToggleAllDay,
          class: _normalizeClass(["relative inline-flex h-6 w-11 items-center rounded-full", __props.modelValue.scheduleAllDay ? 'bg-blue-400' : 'bg-red-500'])
        }, {
          default: _withCtx(() => [
            _createElementVNode("span", {
              class: _normalizeClass([__props.modelValue.scheduleAllDay ? 'translate-x-6' : 'translate-x-1', "inline-block h-4 w-4 transform rounded-full bg-white"])
            }, null, 2)
          ]),
          _: 1
        }, 8, ["model-value", "class"])
      ])
    ]),
    (!__props.modelValue.scheduleAllDay)
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
          _createElementVNode("div", _hoisted_6, [
            _createTextVNode(_toDisplayString(_ctx.$t('messages.coupon.scheduleStartTime')) + " ", 1),
            _createVNode(_component_v_date_picker, {
              mode: "time",
              "model-value": timeStringToDate(__props.modelValue.scheduleStartTime ?? '00:00'),
              "onUpdate:modelValue": _cache[3] || (_cache[3] = (val) => updatetime('scheduleStartTime', dateToTimeString(val))),
              masks: { input: 'HH:mm' },
              is24hr: "",
              locale: "th"
            }, {
              default: _withCtx(({ inputValue, togglePopover }) => [
                _createElementVNode("input", {
                  type: "text",
                  value: inputValue,
                  onClick: togglePopover,
                  onChange: _cache[2] || (_cache[2] = (e) => { updatetime('scheduleStartTime', (e.target as HTMLInputElement).value) }),
                  class: "w-full rounded-md",
                  placeholder: "ชั่วโมง:นาที (HH:mm)"
                }, null, 40, _hoisted_7)
              ]),
              _: 1
            }, 8, ["model-value"])
          ]),
          _createElementVNode("div", _hoisted_8, [
            _createTextVNode(_toDisplayString(_ctx.$t('messages.coupon.scheduleEndTime')) + " ", 1),
            _createVNode(_component_v_date_picker, {
              mode: "time",
              "model-value": timeStringToDate(__props.modelValue.scheduleEndTime ?? '23:59'),
              "onUpdate:modelValue": _cache[5] || (_cache[5] = (val) => updatetime('scheduleEndTime', dateToTimeString(val))),
              masks: { input: 'HH:mm' },
              is24hr: "",
              locale: "th"
            }, {
              default: _withCtx(({ inputValue, togglePopover }) => [
                _createElementVNode("input", {
                  type: "text",
                  value: inputValue,
                  onClick: togglePopover,
                  onChange: _cache[4] || (_cache[4] = (e) => { updatetime('scheduleEndTime', (e.target as HTMLInputElement).value) }),
                  class: "w-full rounded-md",
                  placeholder: "ชั่วโมง:นาที (HH:mm)"
                }, null, 40, _hoisted_9)
              ]),
              _: 1
            }, 8, ["model-value"])
          ])
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_10, [
      _createTextVNode(_toDisplayString(_ctx.$t('messages.coupon.scheduleEndDate')) + " ", 1),
      _createVNode(_component_v_date_picker, {
        mode: "date",
        "model-value": dateStringToDate(__props.modelValue.scheduleEndDate),
        "onUpdate:modelValue": _cache[7] || (_cache[7] = (val) => update('scheduleEndDate', dateToDateString(val))),
        masks: { input: 'DD/MM/YYYY' },
        locale: "th"
      }, {
        default: _withCtx(({ inputValue, togglePopover }) => [
          _createElementVNode("input", {
            type: "text",
            value: inputValue,
            onClick: togglePopover,
            onChange: _cache[6] || (_cache[6] = (e) => { onChangeEndDate((e.target as HTMLInputElement).value) }),
            class: "w-full rounded-md",
            placeholder: "วัน/เดือน/ปี (DD/MM/YYYY)"
          }, null, 40, _hoisted_11)
        ]),
        _: 1
      }, 8, ["model-value"]),
      (endDateError.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_12, _toDisplayString(endDateError.value), 1))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_13, [
      _createTextVNode(_toDisplayString(_ctx.$t('messages.coupon.scheduleRepeatEvery')) + " ", 1),
      _createElementVNode("div", _hoisted_14, [
        _createElementVNode("input", {
          type: "number",
          value: __props.modelValue.scheduleRepeatAmount ?? 1,
          onChange: _cache[8] || (_cache[8] = ($event: any) => (update('scheduleRepeatAmount', Number(($event.target as HTMLInputElement).value)))),
          min: "1",
          class: "w-16 rounded-md"
        }, null, 40, _hoisted_15),
        _createVNode(DropdownComponent, {
          class: "flex-1",
          options: _unref(repeatUnitOptions),
          modelValue: _unref(selectedRepeatUnit),
          onOnOptionSelected: onSelectRepeatUnit
        }, null, 8, ["options", "modelValue"])
      ])
    ]),
    (__props.modelValue.scheduleRepeatUnit === _unref(CouponScheduleRepeatUnitEnum).MONTH)
      ? (_openBlock(), _createElementBlock("div", _hoisted_16, " On day " + _toDisplayString(_unref(dayOfMonth)), 1))
      : _createCommentVNode("", true),
    (__props.modelValue.scheduleRepeatUnit === _unref(CouponScheduleRepeatUnitEnum).WEEK)
      ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
          _createTextVNode(_toDisplayString(_ctx.$t('messages.coupon.scheduleDaysOfWeek')) + " ", 1),
          _createElementVNode("div", _hoisted_18, [
            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(ALL_DAYS, (day) => {
              return _createElementVNode("button", {
                key: day,
                type: "button",
                onClick: ($event: any) => (toggleDay(day)),
                class: _normalizeClass([
            'w-8 h-8 rounded text-sm font-medium',
            __props.modelValue.scheduleDaysOfWeek.includes(day)
              ? 'bg-blue-400 text-white'
              : 'bg-gray-100 text-gray-600'
          ])
              }, _toDisplayString(_ctx.$t(`messages.coupon.day_${day}`)), 11, _hoisted_19)
            }), 64))
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})